import React, { useState, useRef, useEffect } from 'react';
import { Search, Filter, X, ChevronDown, RotateCcw, Calendar, Hash } from 'lucide-react';

// ─── Multi-select Dropdown ──────
const MultiSelect = ({ label, options, selected, onChange, searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => !q || o.toLowerCase().includes(q.toLowerCase()));
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-xs border border-gray-200 rounded-lg hover:border-gray-300 bg-white" data-testid={`filter-ms-${label}`}>
        <span className="truncate text-gray-700">{selected.length > 0 ? `${label} (${selected.length})` : label}</span>
        <ChevronDown size={13} className="text-gray-400 ml-1 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <input type="text" placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-teal-400 outline-none" />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No options</p>}
            {filtered.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 truncate">{opt}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button onClick={() => onChange([])} className="text-[10px] text-red-500 hover:underline">Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Boolean Select ──────
const BoolSelect = ({ label, value, onChange }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value || null)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:border-gray-300" data-testid={`filter-bool-${label}`}>
    <option value="">{label}</option>
    <option value="yes">Yes</option>
    <option value="no">No</option>
  </select>
);

// ─── Range Input ──────
const RangeInput = ({ label, range, onChange }) => (
  <div className="flex items-center gap-1">
    <input type="number" placeholder="Min" value={range?.min || ''} onChange={e => onChange({ ...range, min: e.target.value || null })} className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg" data-testid={`filter-range-min-${label}`} />
    <span className="text-gray-400 text-xs">-</span>
    <input type="number" placeholder="Max" value={range?.max || ''} onChange={e => onChange({ ...range, max: e.target.value || null })} className="w-full px-2 py-2 text-xs border border-gray-200 rounded-lg" data-testid={`filter-range-max-${label}`} />
  </div>
);

// ─── Filter Section ──────
const FilterSection = ({ title, children }) => (
  <div>
    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">{children}</div>
  </div>
);

// ─── Main Filter Bar ──────
const AnalyticsFilterBar = ({ filters, onChange, onApply, onReset, filterOptions, activeCount }) => {
  const [expanded, setExpanded] = useState(false);

  const set = (key, val) => onChange({ ...filters, [key]: val });

  const opts = filterOptions || {};

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="analytics-filter-bar">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Date Range - Closed Won Date */}
        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-gray-400" />
          <span className="text-[10px] font-medium text-gray-500 whitespace-nowrap">Closed Won Date</span>
          <input type="date" value={filters.date_range?.start || ''} onChange={e => set('date_range', { ...filters.date_range, start: e.target.value })}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 outline-none" data-testid="filter-date-start" />
          <span className="text-gray-400 text-xs">to</span>
          <input type="date" value={filters.date_range?.end || ''} onChange={e => set('date_range', { ...filters.date_range, end: e.target.value })}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 outline-none" data-testid="filter-date-end" />
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search property / owner / mobile..." value={filters.search || ''} onChange={e => set('search', e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-400 outline-none" data-testid="filter-global-search" />
        </div>

        {/* Toggle Filters */}
        <button onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${expanded ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          data-testid="filter-toggle-advanced">
          <Filter size={13} />
          Filters
          {activeCount > 0 && <span className="bg-teal-600 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-1">{activeCount}</span>}
        </button>

        {/* Apply */}
        <button onClick={onApply} className="px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors" data-testid="filter-apply">Apply</button>

        {/* Reset */}
        {activeCount > 0 && (
          <button onClick={onReset} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors" data-testid="filter-reset">
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      {/* Active Filter Tags */}
      {activeCount > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {filters.search && <Tag label={`Search: "${filters.search}"`} onRemove={() => { set('search', ''); onApply(); }} />}
          {filters.date_range?.start && <Tag label={`From: ${filters.date_range.start}`} onRemove={() => { set('date_range', { ...filters.date_range, start: '' }); onApply(); }} />}
          {filters.date_range?.end && <Tag label={`To: ${filters.date_range.end}`} onRemove={() => { set('date_range', { ...filters.date_range, end: '' }); onApply(); }} />}
          {(filters.lead_stage || []).map(v => <Tag key={`ls-${v}`} label={`Stage: ${v}`} onRemove={() => { set('lead_stage', filters.lead_stage.filter(x => x !== v)); onApply(); }} />)}
          {(filters.lead_owner || []).map(v => <Tag key={`lo-${v}`} label={`Owner: ${v}`} onRemove={() => { set('lead_owner', filters.lead_owner.filter(x => x !== v)); onApply(); }} />)}
          {(filters.source || []).map(v => <Tag key={`src-${v}`} label={`Source: ${v}`} onRemove={() => { set('source', filters.source.filter(x => x !== v)); onApply(); }} />)}
          {(filters.state || []).map(v => <Tag key={`st-${v}`} label={`State: ${v}`} onRemove={() => { set('state', filters.state.filter(x => x !== v)); onApply(); }} />)}
          {(filters.city || []).map(v => <Tag key={`ct-${v}`} label={`City: ${v}`} onRemove={() => { set('city', filters.city.filter(x => x !== v)); onApply(); }} />)}
          {(filters.property_status || []).map(v => <Tag key={`ps-${v}`} label={`Status: ${v}`} onRemove={() => { set('property_status', filters.property_status.filter(x => x !== v)); onApply(); }} />)}
          {(filters.interested_plan || []).map(v => <Tag key={`ip-${v}`} label={`Plan: ${v}`} onRemove={() => { set('interested_plan', filters.interested_plan.filter(x => x !== v)); onApply(); }} />)}
          {(filters.setup_payment_status || []).map(v => <Tag key={`sp-${v}`} label={`Payment: ${v}`} onRemove={() => { set('setup_payment_status', filters.setup_payment_status.filter(x => x !== v)); onApply(); }} />)}
          {(filters.ota_name || []).map(v => <Tag key={`ota-${v}`} label={`OTA: ${v}`} onRemove={() => { set('ota_name', filters.ota_name.filter(x => x !== v)); onApply(); }} />)}
          {(filters.onboarding_status || []).map(v => <Tag key={`ob-${v}`} label={`Onboarding: ${v}`} onRemove={() => { set('onboarding_status', filters.onboarding_status.filter(x => x !== v)); onApply(); }} />)}
          {filters.has_company && <Tag label={`Company: ${filters.has_company}`} onRemove={() => { set('has_company', null); onApply(); }} />}
          {filters.has_gst && <Tag label={`GST: ${filters.has_gst}`} onRemove={() => { set('has_gst', null); onApply(); }} />}
          {filters.has_pef_pdf && <Tag label={`PEF: ${filters.has_pef_pdf}`} onRemove={() => { set('has_pef_pdf', null); onApply(); }} />}
          {filters.has_google_map && <Tag label={`Map: ${filters.has_google_map}`} onRemove={() => { set('has_google_map', null); onApply(); }} />}
          {filters.has_account_number && <Tag label={`Account: ${filters.has_account_number}`} onRemove={() => { set('has_account_number', null); onApply(); }} />}
        </div>
      )}

      {/* Expanded Advanced Filters */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 space-y-4 pt-4" data-testid="filter-advanced-panel">
          <FilterSection title="Lead Information">
            <MultiSelect label="Lead Stage" options={opts.lead_stage || []} selected={filters.lead_stage || []} onChange={v => set('lead_stage', v)} />
            <MultiSelect label="Lead Owner" options={opts.lead_owner || []} selected={filters.lead_owner || []} onChange={v => set('lead_owner', v)} searchable />
            <MultiSelect label="Source" options={opts.source || []} selected={filters.source || []} onChange={v => set('source', v)} />
            <MultiSelect label="Property Status" options={opts.property_status || []} selected={filters.property_status || []} onChange={v => set('property_status', v)} />
            <MultiSelect label="Onboarding" options={opts.onboarding_status || []} selected={filters.onboarding_status || []} onChange={v => set('onboarding_status', v)} />
          </FilterSection>

          <FilterSection title="Geographical">
            <MultiSelect label="State" options={opts.state || []} selected={filters.state || []} onChange={v => set('state', v)} searchable />
            <MultiSelect label="City" options={opts.city || []} selected={filters.city || []} onChange={v => set('city', v)} searchable />
          </FilterSection>

          <FilterSection title="OTA & Challenges">
            <MultiSelect label="OTA Name" options={opts.ota_name || []} selected={filters.ota_name || []} onChange={v => set('ota_name', v)} />
            <MultiSelect label="OTA Connected" options={opts.existing_ota_connected || []} selected={filters.existing_ota_connected || []} onChange={v => set('existing_ota_connected', v)} />
            <RangeInput label="Rooms" range={filters.rooms_range} onChange={v => set('rooms_range', v)} />
          </FilterSection>

          <FilterSection title="Payment">
            <MultiSelect label="Interested Plan" options={opts.interested_plan || []} selected={filters.interested_plan || []} onChange={v => set('interested_plan', v)} />
            <MultiSelect label="Payment Status" options={opts.setup_payment_status || []} selected={filters.setup_payment_status || []} onChange={v => set('setup_payment_status', v)} />
            <RangeInput label="Setup Fee" range={filters.fee_range} onChange={v => set('fee_range', v)} />
          </FilterSection>

          <FilterSection title="Property / Documents / PEF">
            <BoolSelect label="Has Company" value={filters.has_company} onChange={v => set('has_company', v)} />
            <BoolSelect label="Has GST" value={filters.has_gst} onChange={v => set('has_gst', v)} />
            <BoolSelect label="Google Map" value={filters.has_google_map} onChange={v => set('has_google_map', v)} />
            <BoolSelect label="Drive Link" value={filters.has_drive_link} onChange={v => set('has_drive_link', v)} />
            <BoolSelect label="PEF PDF" value={filters.has_pef_pdf} onChange={v => set('has_pef_pdf', v)} />
          </FilterSection>

          <FilterSection title="Bank Details">
            <MultiSelect label="Bank Name" options={opts.bank_name || []} selected={filters.bank_name || []} onChange={v => set('bank_name', v)} searchable />
            <BoolSelect label="Account Number" value={filters.has_account_number} onChange={v => set('has_account_number', v)} />
            <BoolSelect label="IFSC Code" value={filters.has_ifsc} onChange={v => set('has_ifsc', v)} />
            <BoolSelect label="Cancelled Cheque" value={filters.has_cheque_pdf} onChange={v => set('has_cheque_pdf', v)} />
          </FilterSection>
        </div>
      )}
    </div>
  );
};

const Tag = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-teal-50 text-teal-700 rounded-md" data-testid={`filter-tag-${label}`}>
    {label}
    <button onClick={onRemove} className="hover:text-red-500"><X size={11} /></button>
  </span>
);

export default AnalyticsFilterBar;
